import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import TopBar from '../components/TopBar'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import './onboarding/onboarding.css'

const ResetPassword = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { updatePassword } = useAuth()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  const [checkingLink, setCheckingLink] = useState(true)

  useEffect(() => {
    // Проверяем, что ссылка еще действительна и есть recovery-сессия
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        setError('This password reset link is invalid or has expired. Please request a new one.')
      }
      setCheckingLink(false)
    })
  }, [])

  const onBack = () => {
    navigate('/', { replace: true })
  }

  const onSubmit = async () => {
    if (!password || password.trim().length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    if (/\s/.test(password)) {
      setError('Password must not contain spaces.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setError('')
    setSuccess('')
    setLoading(true)

    try {
      console.log('[ResetPassword] Attempting to update password...')
      
      // Проверяем что есть recovery сессия
      const { data: sessionData } = await supabase.auth.getSession()
      if (!sessionData?.session) {
        setError('This password reset link is invalid or has expired. Please request a new one.')
        setLoading(false)
        return
      }

      console.log('[ResetPassword] Session found, updating password...')
      const { user, error: updateError } = await updatePassword(password)
      console.log('[ResetPassword] Update result:', { hasUser: !!user, error: updateError })

      if (updateError) {
        setError(updateError || 'Error updating password. Please try again.')
        setLoading(false)
        return
      }

      if (!user) {
        setError('Password was not updated. Please try again.')
        setLoading(false)
        return
      }

      console.log('[ResetPassword] ✅ Password updated successfully, user ID:', user.id)
      
      // Сбрасываем loading сразу после успешного обновления
      setLoading(false)
      setSuccess('Your password has been updated successfully!')

      // Даем время для установки сессии
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Проверяем сессию перед навигацией
      const { data: finalSession } = await supabase.auth.getSession()
      console.log('[ResetPassword] Final session check:', { hasSession: !!finalSession?.session, userId: finalSession?.session?.user?.id })
      
      if (finalSession?.session?.user) {
        console.log('[ResetPassword] ✅ Session confirmed, navigating to /app')
        window.location.href = '/app'
      } else {
        console.log('[ResetPassword] ⚠️ Session not found, but password was updated. Navigating anyway...')
        // Пароль обновлен, но сессия не установлена - пробуем войти
        // Но у нас нет пароля здесь, поэтому просто переходим
        window.location.href = '/app'
      }
    } catch (err) {
      console.error('[ResetPassword] Exception:', err)
      setError(err?.message || 'Error updating password. Please try again.')
      setLoading(false)
    }
  }

  if (checkingLink) {
    return (
      <div className="onboarding-page">
        <TopBar currentStep={0} totalSteps={0} showBack={false} />
        <div className="onboarding-content">
          <h1 className="h1">{t('resetPassword.resetting')}</h1>
          <p className="p">{t('resetPassword.pleaseWait')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="onboarding-page">
      <TopBar currentStep={0} totalSteps={0} onBack={onBack} showBack={true} />
      <div className="onboarding-content">
        <h1 className="h1">{t('resetPassword.createNewPassword')}</h1>
        <p className="p">
          {t('resetPassword.createNewPasswordSubtitle')}
        </p>

        <div className="field">
          <label className="label">{t('resetPassword.newPasswordLabel')}</label>
          <input
            className={`input ${error ? 'error' : ''}`}
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              if (error) setError('')
            }}
            placeholder="••••••••"
          />
        </div>

        <div className="field">
          <label className="label">{t('resetPassword.confirmPasswordLabel')}</label>
          <input
            className={`input ${error ? 'error' : ''}`}
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value)
              if (error) setError('')
            }}
            placeholder="••••••••"
          />
        </div>

        {error ? <div className="error-text">{error}</div> : null}
        {success ? <div className="success-text">{success}</div> : null}
      </div>

      <button className="cta-fixed" onClick={onSubmit} disabled={loading || checkingLink}>
        {loading ? t('resetPassword.updatingPassword') : t('resetPassword.saveNewPassword')}
      </button>
    </div>
  )
}

export default ResetPassword

